import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, Link } from "wouter";
import {
  ShieldAlert,
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ChevronLeft,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PlatformLayout } from "./platform-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PiiScanRun {
  id: string;
  status: "pending" | "running" | "completed" | "failed";
  triggerType: "scheduled" | "manual";
  triggeredByEmail: string | null;
  startedAt: string;
  finishedAt: string | null;
  rowsScanned: number;
  findingsCount: number;
  errorMessage: string | null;
}

interface PiiScanFinding {
  id: string;
  workspaceId: string | null;
  workspaceName: string | null;
  workspaceSlug: string | null;
  tableName: string;
  columnName: string;
  rowId: string;
  patternKind: string;
  redactedSnippet: string;
  matchCount: number;
  detectedAt: string | null;
}

interface PiiScanRunDetail {
  run: PiiScanRun;
  totalsByTable: Array<{ tableName: string; columnName: string; findings: number }>;
  findings: PiiScanFinding[];
}

function formatDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status }: { status: PiiScanRun["status"] }) {
  if (status === "completed") {
    return (
      <Badge variant="default" className="gap-1">
        <CheckCircle2 className="w-3 h-3" /> completed
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="w-3 h-3" /> failed
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      <Loader2 className="w-3 h-3 animate-spin" /> {status}
    </Badge>
  );
}

function RunDetailView({ runId }: { runId: string }) {
  const { data, isLoading, refetch } = useQuery<PiiScanRunDetail>({
    queryKey: ["/api/platform/pii-scans", runId],
    refetchInterval: (q) => {
      const status = (q.state.data as PiiScanRunDetail | undefined)?.run.status;
      return status === "running" || status === "pending" ? 3000 : false;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) {
    return <div className="text-muted-foreground">Run not found.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href="/platform/pii-scans">
            <Button variant="ghost" size="sm" data-testid="link-back-to-runs">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to runs
            </Button>
          </Link>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          data-testid="button-refresh-run"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5" /> Scan run
                <code className="text-xs ml-2" data-testid="text-run-id">{data.run.id.slice(0, 8)}…</code>
              </CardTitle>
              <CardDescription>
                {data.run.triggerType === "scheduled" ? "Scheduled (quarterly)" : `Manual — ${data.run.triggeredByEmail ?? "unknown"}`}
              </CardDescription>
            </div>
            <StatusBadge status={data.run.status} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Started</div>
              <div data-testid="text-run-started">{formatDate(data.run.startedAt)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Finished</div>
              <div data-testid="text-run-finished">{formatDate(data.run.finishedAt)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Rows scanned</div>
              <div data-testid="text-rows-scanned">{data.run.rowsScanned.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Findings</div>
              <div className="font-semibold" data-testid="text-findings-count">
                {data.run.findingsCount.toLocaleString()}
              </div>
            </div>
          </div>
          {data.run.errorMessage && (
            <div className="mt-4 p-3 rounded bg-red-50 dark:bg-red-950/30 text-sm text-red-800 dark:text-red-200">
              {data.run.errorMessage}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Totals by column</CardTitle>
          <CardDescription>Where the scanner saw email / phone / SSN-shaped values.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.totalsByTable.length === 0 ? (
            <div className="text-muted-foreground text-sm">No findings.</div>
          ) : (
            <div className="space-y-2">
              {data.totalsByTable.map((t) => (
                <div
                  key={`${t.tableName}.${t.columnName}`}
                  className="flex items-center justify-between p-3 rounded border"
                  data-testid={`totals-${t.tableName}-${t.columnName}`}
                >
                  <div className="font-mono text-sm">
                    {t.tableName}.<span className="font-semibold">{t.columnName}</span>
                  </div>
                  <Badge variant="secondary">{t.findings} findings</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Findings (first 1,000)</CardTitle>
          <CardDescription>
            Snippets are redacted server-side — the raw value is never sent to the browser.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.findings.length === 0 ? (
            <div className="text-muted-foreground text-sm">No findings.</div>
          ) : (
            <ScrollArea className="h-[480px]">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1 pr-3">Workspace</th>
                    <th className="py-1 pr-3">Table.column</th>
                    <th className="py-1 pr-3">Row id</th>
                    <th className="py-1 pr-3">Pattern</th>
                    <th className="py-1 pr-3">Snippet</th>
                    <th className="py-1 pr-3">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.findings.map((f) => (
                    <tr key={f.id} className="border-t align-top" data-testid={`finding-${f.id}`}>
                      <td className="py-1 pr-3" data-testid={`finding-workspace-${f.id}`}>
                        {f.workspaceId ? (
                          <Link
                            href={`/platform/workspaces/${f.workspaceId}`}
                            className="text-primary hover:underline"
                            data-testid={`link-workspace-${f.id}`}
                          >
                            {f.workspaceName ?? f.workspaceSlug ?? f.workspaceId}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-1 pr-3 font-mono text-xs">
                        {f.tableName}.{f.columnName}
                      </td>
                      <td className="py-1 pr-3 font-mono text-xs" data-testid={`finding-rowid-${f.id}`}>
                        <span className="select-all break-all">{f.rowId}</span>
                      </td>
                      <td className="py-1 pr-3">
                        <Badge variant="outline">{f.patternKind}</Badge>
                      </td>
                      <td className="py-1 pr-3 font-mono text-xs" data-testid={`finding-snippet-${f.id}`}>
                        {f.redactedSnippet}
                      </td>
                      <td className="py-1 pr-3">{f.matchCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RunsListView() {
  const { toast } = useToast();
  const { data, isLoading, refetch } = useQuery<{ runs: PiiScanRun[] }>({
    queryKey: ["/api/platform/pii-scans"],
    refetchInterval: 5000,
  });

  const runMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/platform/pii-scans/run"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/pii-scans"] });
      toast({ title: "Scan started", description: "The scan is running in the background." });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to start scan",
        description: err?.message || "Unknown error",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">PII Column Scans</h1>
          <p className="text-muted-foreground text-sm">
            Quarterly sweep of unencrypted free-text columns for email / phone / SSN-shaped values.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            data-testid="button-refresh-runs"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
            data-testid="button-run-scan-now"
          >
            {runMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" /> Run scan now
              </>
            )}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent runs</CardTitle>
          <CardDescription>Newest first. Click a row to see findings.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : !data?.runs?.length ? (
            <div className="text-muted-foreground text-sm">
              No scan runs yet. Click <strong>Run scan now</strong> to trigger one.
            </div>
          ) : (
            <div className="space-y-2">
              {data.runs.map((run) => (
                <Link
                  key={run.id}
                  href={`/platform/pii-scans/${run.id}`}
                  data-testid={`row-run-${run.id}`}
                >
                  <div className="flex items-center justify-between p-3 rounded border hover-elevate active-elevate-2 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <ShieldAlert className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">
                          {formatDate(run.startedAt)}
                          <span className="text-xs text-muted-foreground ml-2">
                            {run.triggerType === "scheduled" ? "scheduled" : "manual"}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {run.rowsScanned.toLocaleString()} rows scanned •{" "}
                          <span className="font-semibold">{run.findingsCount.toLocaleString()}</span> findings
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={run.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function PiiScansContent() {
  const [, params] = useRoute<{ runId: string }>("/platform/pii-scans/:runId");
  const runId = params?.runId;

  return (
    <>
      <div className="p-3 sm:p-6">
        {runId ? <RunDetailView runId={runId} /> : <RunsListView />}
      </div>
    </>
  );
}

export default function PlatformPiiScans() {
  return <PlatformLayout><PiiScansContent /></PlatformLayout>;
}
